import React, { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw, Trophy, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { EmptyState, ErrorState } from '@/components/app';
import {
  gamesAdminApi,
  type LeaderboardRow,
  type SparringPartnerRow,
  type SparringProfile,
  type SparringProfileStats,
  type SparringSettings,
} from '../../api/games';
import {
  Card,
  NumberField,
  SaveButton,
  Stat,
  TabIntro,
  TextField,
  Toggle,
} from './controls';
import { getErrorMessage } from '@/lib/errors';
import { toast } from 'sonner';
import { useConfirm } from '@/components/shared/ConfirmDialog';

/**
 * Вкладка «Спарринг» — управление искусственными соперниками в дуэлях.
 * Сила настраивается пятью профилями, имена живут отдельным пулом, поэтому
 * игрок видит в истории разные ники. См. ADR 0018.
 */
export default function SparringTab() {
  const [settings, setSettings] = useState<SparringSettings | null>(null);
  const [profiles, setProfiles] = useState<SparringProfile[]>([]);
  const [partners, setPartners] = useState<SparringPartnerRow[]>([]);
  const [stats, setStats] = useState<SparringProfileStats[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // allSettled, а не all: упавший лидерборд не должен прятать профили и
      // настройки — вкладка обязана оставаться рабочей по частям.
      const [s, p, allPartners, st, lb] = await Promise.allSettled([
        gamesAdminApi.sparringSettings(),
        gamesAdminApi.sparringProfiles(),
        gamesAdminApi.sparringPartners(),
        gamesAdminApi.sparringStats(),
        gamesAdminApi.duelLeaderboard('all'),
      ]);

      if (s.status === 'fulfilled') setSettings(s.value);
      if (p.status === 'fulfilled') setProfiles(p.value);
      if (allPartners.status === 'fulfilled') setPartners(allPartners.value);
      if (st.status === 'fulfilled') setStats(st.value);
      if (lb.status === 'fulfilled') setLeaderboard(lb.value.rows ?? []);

      const failed = [s, p, allPartners, st, lb].filter(
        (r) => r.status === 'rejected',
      );
      if (failed.length) {
        setError(`Не удалось загрузить часть данных (${failed.length} из 5)`);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Не удалось загрузить'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex flex-col gap-4">
      <TabIntro>
        Дуэль требует двух игроков, но живой соперник находится далеко не всегда.
        Чтобы человек не играл в пустоту, через несколько секунд поиска ему
        подсаживается <b>спарринг-партнёр</b> — искусственный соперник. В
        приложении он выглядит обычным игроком и ничем не помечен.
        <br />
        <br />
        Сила задаётся <b>профилем</b> (их пять, по одному на ранг), а имя берётся
        из общего пула — поэтому в истории дуэлей игрок видит разные ники, а
        настраивать нужно всего пять карточек. Рейтинг партнёра — константа: он
        не растёт и не падает от матчей и никогда не попадает в лидерборд.
      </TabIntro>

      <div className="flex items-center justify-end">
        <Button type="button" variant="secondary" onClick={load} disabled={loading}>
          <RefreshCw className={cn(loading && 'animate-spin')} aria-hidden />
          Обновить
        </Button>
      </div>

      {error && (
        <ErrorState compact message={error} onRetry={load} className="rounded-xl bg-neg-soft px-3.5 py-3" />
      )}

      {settings && (
        <SettingsCard
          settings={settings}
          onSaved={(next) => setSettings(next)}
        />
      )}

      <div className="flex flex-col gap-3">
        {profiles.map((profile) => (
          <ProfileCard
            key={profile.id}
            profile={profile}
            stats={stats.find((s) => s.profileId === profile.id) ?? null}
            partners={partners.filter((p) => p.profileId === profile.id)}
            onProfileSaved={(next) =>
              setProfiles((prev) =>
                prev.map((p) => (p.id === next.id ? next : p)),
              )
            }
            onPartnerAdded={(partner) =>
              setPartners((prev) => [...prev, partner])
            }
            onPartnerRetired={(id) =>
              setPartners((prev) => prev.filter((p) => p.id !== id))
            }
          />
        ))}
      </div>

      <LeaderboardCard rows={leaderboard} onRefresh={load} />
    </div>
  );
}

// ─── Настройки фичи ─────────────────────────────────────────────────────────

function SettingsCard({
  settings,
  onSaved,
}: {
  settings: SparringSettings;
  onSaved: (next: SparringSettings) => void;
}) {
  const [draft, setDraft] = useState<SparringSettings>(settings);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => setDraft(settings), [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const next = await gamesAdminApi.updateSparringSettings(draft);
      onSaved(next);
      setSavedAt(Date.now());
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, 'Не удалось сохранить настройки спарринга'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Настройки">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <Toggle
          label="Спарринг включён"
          hint="Главный рубильник. Выключишь — дуэли вернутся к прежнему поведению: игрок сыграет и будет ждать живого соперника до суток."
          value={draft.enabled}
          onChange={(enabled) => setDraft({ ...draft, enabled })}
        />
        <NumberField
          label="Окно поиска живого, мс"
          hint="Сколько секунд держим игрока в поиске, давая шанс встретиться с живым человеком. Всё это время он видит спиннер, поэтому больше 5 секунд ставить не стоит. Живой соперник всегда в приоритете."
          value={draft.search_window_ms}
          min={0}
          max={15000}
          onChange={(search_window_ms) => setDraft({ ...draft, search_window_ms })}
        />
        <NumberField
          label="Рейтинговых дуэлей с партнёром в сутки"
          hint="Сколько матчей против партнёров за день меняют рейтинг игрока. Дальше матчи идут как тренировка: результат показывается, ELO стоит. Защищает лидерборд от премиум-игроков, которые могли бы фармить рейтинг об ботов без ограничений."
          value={draft.rated_cap_per_day}
          min={0}
          max={50}
          onChange={(rated_cap_per_day) =>
            setDraft({ ...draft, rated_cap_per_day })
          }
        />
        <Toggle
          label="Добивать зависшие матчи"
          hint="Если игрок сыграл, а соперник так и не пришёл, партнёр доигрывает за него: матч закрывается, человек получает пуш и возвращается посмотреть результат."
          value={draft.backfill_enabled}
          onChange={(backfill_enabled) =>
            setDraft({ ...draft, backfill_enabled })
          }
        />
        <NumberField
          label="Добивать через, мин"
          hint="Сколько ждать живого соперника, прежде чем матч добьёт партнёр. Слишком мало — задавим живой PvP, слишком много — человек забудет, что вообще играл."
          value={draft.backfill_after_minutes}
          min={1}
          max={1440}
          onChange={(backfill_after_minutes) =>
            setDraft({ ...draft, backfill_after_minutes })
          }
        />
        <NumberField
          label="Матчей за один прогон"
          hint="Крон добивает матчи пачками каждые 5 минут. Это ограничение не даёт разослать сотни пушей одновременно."
          value={draft.backfill_batch_size}
          min={1}
          max={500}
          onChange={(backfill_batch_size) =>
            setDraft({ ...draft, backfill_batch_size })
          }
        />
      </div>

      <div className="flex items-center gap-3 mt-5">
        <SaveButton saving={saving} onClick={save} />
        {savedAt && (
          <span role="status" className="text-[13px] font-semibold text-pos">Сохранено</span>
        )}
      </div>
    </Card>
  );
}

// ─── Профиль сложности ──────────────────────────────────────────────────────

function ProfileCard({
  profile,
  stats,
  partners,
  onProfileSaved,
  onPartnerAdded,
  onPartnerRetired,
}: {
  profile: SparringProfile;
  stats: SparringProfileStats | null;
  partners: SparringPartnerRow[];
  onProfileSaved: (next: SparringProfile) => void;
  onPartnerAdded: (partner: SparringPartnerRow) => void;
  onPartnerRetired: (id: string) => void;
}) {
  const [draft, setDraft] = useState<SparringProfile>(profile);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const confirm = useConfirm();
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => setDraft(profile), [profile]);

  const save = async () => {
    setSaving(true);
    try {
      const next = await gamesAdminApi.updateSparringProfile(profile.id, {
        title: draft.title,
        elo_rating: draft.elo_rating,
        accuracy: draft.accuracy,
        tricky_penalty: draft.tricky_penalty,
        min_answer_ms: draft.min_answer_ms,
        max_answer_ms: draft.max_answer_ms,
        weight: draft.weight,
        is_active: draft.is_active,
      });
      onProfileSaved(next);
      toast.success('Профиль сохранён');
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, 'Не удалось сохранить профиль'));
    } finally {
      setSaving(false);
    }
  };

  const addPartner = async () => {
    const name = newName.trim();
    if (!name) return;
    setAddError(null);
    try {
      const created = await gamesAdminApi.addSparringPartner(profile.id, name);
      onPartnerAdded(created);
      setNewName('');
    } catch (err: unknown) {
      setAddError(getErrorMessage(err, 'Не удалось добавить'));
    }
  };

  const retire = async (id: string, gamerTag: string | null) => {
    const ok = await confirm({
      title: gamerTag ? `Вывести партнёра «${gamerTag}» из подбора?` : "Вывести партнёра из подбора?",
      description: 'Игроки больше не будут попадать на этого спарринг-партнёра. История его матчей сохранится.',
      confirmLabel: 'Вывести',
      destructive: true,
    });
    if (!ok) return;
    try {
      await gamesAdminApi.retireSparringPartner(id);
      onPartnerRetired(id);
      toast.success('Партнёр выведен из подбора');
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, 'Не удалось вывести партнёра'));
    }
  };

  // Целевой коридор — 55–60% побед игрока. Отклонение подсвечиваем, чтобы
  // калибровать точность профиля по факту, а не на глаз.
  const winRate = stats?.userWinRate ?? 0;
  const winRateTone =
    !stats || stats.matches < 20
      ? 'neutral'
      : winRate >= 50 && winRate <= 65
        ? 'pos'
        : 'neg';

  return (
    <Card
      title={`${draft.title} · ${draft.code}`}
      dimmed={!draft.is_active}
    >
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="flex flex-col gap-4 xl:col-span-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <TextField
              label="Название"
              value={draft.title}
              onChange={(title) => setDraft({ ...draft, title })}
            />
            <NumberField
              label="ELO"
              hint="Рейтинг партнёра. Определяет, каким игрокам он попадётся: подбор идёт в пределах своего ранга и соседних. Матчи это число не меняют."
              value={draft.elo_rating}
              min={0}
              max={3000}
              onChange={(elo_rating) => setDraft({ ...draft, elo_rating })}
            />
            <NumberField
              label="Точность, %"
              hint="Вероятность верного ответа. Главный рычаг сложности: 50% — слабый соперник, 90% — почти безошибочный. Ориентир: игроки должны выигрывать 55–60% матчей."
              value={Math.round(draft.accuracy * 100)}
              min={5}
              max={100}
              onChange={(v) => setDraft({ ...draft, accuracy: v / 100 })}
            />
            <NumberField
              label="Штраф на хитрых, %"
              hint="Насколько падает точность на вопросах с пометкой «хитрый». Благодаря этому партнёр ошибается там же, где ошибаются живые люди, а не случайно."
              value={Math.round(draft.tricky_penalty * 100)}
              min={0}
              max={60}
              onChange={(v) => setDraft({ ...draft, tricky_penalty: v / 100 })}
            />
            <NumberField
              label="Мин. время ответа, мс"
              hint="Нижняя граница раздумий над одним вопросом. Слишком мало — выдаёт бота: человек не отвечает за секунду. 1000 мс = 1 секунда."
              value={draft.min_answer_ms}
              min={500}
              max={60000}
              onChange={(min_answer_ms) => setDraft({ ...draft, min_answer_ms })}
            />
            <NumberField
              label="Макс. время ответа, мс"
              hint="Верхняя граница раздумий. Общее время складывается из десяти таких интервалов и решает исход при равном счёте — кто быстрее, тот и выиграл."
              value={draft.max_answer_ms}
              min={500}
              max={120000}
              onChange={(max_answer_ms) => setDraft({ ...draft, max_answer_ms })}
            />
            <NumberField
              label="Вес в подборе"
              hint="Насколько часто этот профиль выпадает среди подходящих по рангу. Больше вес — чаще встречается."
              value={draft.weight}
              min={1}
              max={10}
              onChange={(weight) => setDraft({ ...draft, weight })}
            />
            <Toggle
              label="Активен"
              hint="Выключенный профиль не участвует в подборе, но его имена и история матчей сохраняются."
              value={draft.is_active}
              onChange={(is_active) => setDraft({ ...draft, is_active })}
            />
          </div>

          <SaveButton saving={saving} onClick={save} />

          <div className="flex flex-col gap-2 border-t border-border-soft pt-4">
            <span className="text-[13px] font-semibold">
              Имена <span className="font-medium tabular-nums text-text-2">{partners.length}</span>
            </span>
            {partners.length > 0 && (
              <ul aria-label="Имена партнёров" className="m-0 flex list-none flex-wrap gap-2 p-0">
                {partners.map((p) => (
                  <li key={p.id}>
                    <span className="inline-flex h-[30px] items-center gap-1.5 rounded-full bg-muted pl-3 pr-1.5 text-[13px] font-semibold">
                      {p.gamerTag}
                      <button
                        type="button"
                        onClick={() => retire(p.id, p.gamerTag)}
                        title="Вывести из подбора (история сохранится)"
                        aria-label={`Вывести ${p.gamerTag ?? 'партнёра'} из подбора`}
                        className="grid size-[22px] place-items-center rounded-full text-text-2 outline-none hover:bg-surface hover:text-neg focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <X className="size-3.5" aria-hidden />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addPartner();
                  }
                }}
                placeholder="Новое имя"
                aria-label={`Новое имя для профиля ${draft.title}`}
                aria-invalid={addError ? true : undefined}
                maxLength={20}
                className="w-48"
              />
              <Button type="button" variant="secondary" onClick={addPartner} disabled={!newName.trim()}>
                <Plus aria-hidden />
                Добавить
              </Button>
              {addError && (
                <span role="alert" className="text-[13px] font-semibold text-neg">{addError}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Stat
            label="Матчей"
            value={String(stats?.matches ?? 0)}
            tooltip="Сколько дуэлей против партнёров этого профиля уже сыграно. Пока матчей меньше 20, проценту побед верить рано."
          />
          <Stat
            label="Побед игроков"
            value={`${winRate}%`}
            tone={winRateTone}
            tooltip="Как часто живые игроки побеждают этот профиль. Зелёный — норма. Сильно выше коридора означает, что партнёр слаб и рейтинги поедут вверх; сильно ниже — слишком силён и это демотивирует. Правится полем «Точность»."
            hint={
              stats && stats.matches < 20
                ? 'мало данных'
                : 'целевой коридор 55–60%'
            }
          />
          <Stat
            tooltip="Средний счёт из 10 вопросов: слева живые игроки, справа партнёры этого профиля. Удобно для тонкой настройки точности."
            label="Счёт игрок / партнёр"
            value={`${(stats?.avgUserScore ?? 0).toFixed(1)} : ${(
              stats?.avgPartnerScore ?? 0
            ).toFixed(1)}`}
          />
          <Stat
            tooltip="Итоги всех матчей против этого профиля глазами живого игрока."
            label="Победы / поражения / ничьи"
            value={`${stats?.userWins ?? 0} / ${stats?.userLosses ?? 0} / ${
              stats?.draws ?? 0
            }`}
          />
        </div>
      </div>
    </Card>
  );
}

// ─── Лидерборд ──────────────────────────────────────────────────────────────

function LeaderboardCard({
  rows,
  onRefresh,
}: {
  rows: LeaderboardRow[];
  onRefresh: () => void;
}) {
  const [busy, setBusy] = useState(false);

  const rebuild = async () => {
    setBusy(true);
    try {
      await gamesAdminApi.refreshLeaderboards();
      onRefresh();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, 'Не удалось пересобрать лидерборд'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Лидерборд дуэлей — то, что видят игроки"
      icon={<Trophy />}
    >
      {rows.length === 0 ? (
        <EmptyState title="Пока пусто" description="Ни одной завершённой рейтинговой дуэли." className="py-6" />
      ) : (
        <Table className="min-w-[420px]" containerClassName="-mx-[18px] w-auto">
          <TableHeader>
            <TableRow className="border-t-0">
              <TableHead className="w-16 text-right">#</TableHead>
              <TableHead>Игрок</TableHead>
              <TableHead className="text-right">ELO</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.slice(0, 25).map((row) => (
              <TableRow key={row.userId}>
                <TableCell className="text-right tabular-nums text-text-2">{row.rank}</TableCell>
                <TableCell className="font-semibold">{row.gamerTag}</TableCell>
                <TableCell className="text-right tabular-nums">{row.score}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="m-0 max-w-2xl text-xs text-text-2">
          В таблицу попадают только живые игроки: спарринг-партнёры,
          заблокированные и удалившие аккаунт исключены. Данные кэшируются до
          часа — после блокировки читера жми «Пересобрать», чтобы увидеть
          результат сразу.
        </p>
        <Button type="button" variant="secondary" onClick={rebuild} disabled={busy}>
          <RefreshCw className={cn(busy && 'animate-spin')} aria-hidden />
          {busy ? 'Пересобираю…' : 'Пересобрать'}
        </Button>
      </div>
    </Card>
  );
}
